import React, { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { Transition } from '@headlessui/react';
import {
  ChevronUpDownIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { COUNTRIES, PRIORITY_COUNTRIES } from '../../data/countries';

// --- Helpers ---

function findCountryByCode(code) {
  return COUNTRIES.find(c => c.code === code);
}

function findCountryByDialCode(phoneValue, defaultCountryCode) {
  if (!phoneValue || !phoneValue.startsWith('+')) return null;
  const cleaned = phoneValue.replace(/[^\d+]/g, '');
  const defaultCountry = findCountryByCode(defaultCountryCode);
  const sorted = [...COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length);

  for (const country of sorted) {
    if (cleaned.startsWith(country.dialCode)) {
      if (defaultCountry && defaultCountry.dialCode === country.dialCode) {
        return defaultCountry;
      }
      return country;
    }
  }
  return null;
}

function formatDisplayNumber(phone) {
  const country = findCountryByDialCode(phone, 'US');
  if (country) {
    const national = phone.slice(country.dialCode.length);
    return { flag: country.flag, dialCode: country.dialCode, national, country };
  }
  return { flag: '', dialCode: '', national: phone, country: null };
}

function loadSavedNumbers(storageKey) {
  try {
    const data = localStorage.getItem(storageKey);
    if (!data) return [];
    const parsed = JSON.parse(data);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveSavedNumbers(storageKey, numbers) {
  localStorage.setItem(storageKey, JSON.stringify(numbers));
}

// --- Component ---

const PhoneInput = React.forwardRef(({
  value = '',
  onChange,
  defaultCountry = 'MX',
  storageKey = 'phone_numbers',
  id,
  placeholder = '1234567890',
  required = false,
  disabled = false,
  className,
}, ref) => {
  const { t } = useTranslation();
  const [savedNumbers, setSavedNumbers] = useState(() => loadSavedNumbers(storageKey));
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [countryQuery, setCountryQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(
    () => findCountryByCode(defaultCountry) || COUNTRIES[0]
  );
  const [newNumber, setNewNumber] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const numberInputRef = useRef(null);
  const countrySearchRef = useRef(null);
  const containerRef = useRef(null);

  // Sync saved numbers to localStorage
  useEffect(() => {
    saveSavedNumbers(storageKey, savedNumbers);
  }, [savedNumbers, storageKey]);

  // Filtered countries for the country selector
  const filteredCountries = useMemo(() => {
    if (!countryQuery) return COUNTRIES;
    const q = countryQuery.toLowerCase();
    return COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.dialCode.includes(q) ||
      c.code.toLowerCase().includes(q)
    );
  }, [countryQuery]);

  const priorityList = filteredCountries.filter(c => PRIORITY_COUNTRIES.includes(c.code));
  const otherList = filteredCountries.filter(c => !PRIORITY_COUNTRIES.includes(c.code));

  // Get display info for current value
  const currentDisplay = value ? formatDisplayNumber(value) : null;

  const handleSelectSaved = (phone) => {
    onChange?.(phone);
    setIsDropdownOpen(false);
  };

  const handleDeleteSaved = (e, phone) => {
    e.stopPropagation();
    const updated = savedNumbers.filter(n => n.phone !== phone);
    setSavedNumbers(updated);
    if (value === phone) {
      onChange?.('');
    }
  };

  const handleStartAdd = () => {
    setIsAddMode(true);
    setIsDropdownOpen(false);
    setSelectedCountry(findCountryByCode(defaultCountry) || COUNTRIES[0]);
    setNewNumber('');
    setNewLabel('');
    setTimeout(() => numberInputRef.current?.focus(), 50);
  };

  const handleSaveNew = () => {
    const digits = newNumber.replace(/\D/g, '');
    if (!digits) return;

    // Guard against double prefix: if user typed the dial code digits, strip them
    const dialDigits = selectedCountry.dialCode.replace(/\D/g, '');
    const nationalDigits = digits.startsWith(dialDigits) ? digits.slice(dialDigits.length) : digits;
    const fullNumber = `${selectedCountry.dialCode}${nationalDigits}`;
    const label = newLabel.trim() || `${selectedCountry.flag} ${fullNumber}`;

    // Don't add duplicates
    if (!savedNumbers.find(n => n.phone === fullNumber)) {
      setSavedNumbers(prev => [...prev, {
        phone: fullNumber,
        label,
        countryCode: selectedCountry.code,
      }]);
    }

    onChange?.(fullNumber);
    setIsAddMode(false);
    setNewNumber('');
    setNewLabel('');
  };

  const handleCancelAdd = () => {
    setIsAddMode(false);
    setNewNumber('');
    setNewLabel('');
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsCountryOpen(false);
    setCountryQuery('');
    setTimeout(() => numberInputRef.current?.focus(), 50);
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').trim();
    if (pasted.startsWith('+')) {
      e.preventDefault();
      const country = findCountryByDialCode(pasted, defaultCountry);
      if (country) {
        setSelectedCountry(country);
        setNewNumber(pasted.slice(country.dialCode.length));
      }
    }
  };

  // --- Add Mode: country selector + number input ---
  if (isAddMode) {
    return (
      <div className={cn('mt-1 space-y-2', className)}>
        {/* Country + Number row */}
        <div className="flex">
          {/* Country selector */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setIsCountryOpen(prev => !prev);
                setCountryQuery('');
                if (!isCountryOpen) {
                  setTimeout(() => countrySearchRef.current?.focus(), 50);
                }
              }}
              className="flex items-center gap-1.5 px-3 rounded-l-lg border border-r-0 border-border bg-card text-sm transition-colors duration-200 hover:bg-hover focus:outline-none focus:ring-2 focus:ring-primary focus:z-10"
              style={{ minHeight: '38px' }}
            >
              <span className="text-lg leading-none">{selectedCountry.flag}</span>
              <span className="text-sm font-medium text-foreground">{selectedCountry.dialCode}</span>
              <ChevronUpDownIcon className="h-4 w-4" style={{ color: 'var(--muted)' }} />
            </button>

            {/* Country dropdown */}
            <Transition
              show={isCountryOpen}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="absolute left-0 top-full z-50 mt-1 w-72 max-h-64 overflow-auto rounded-xl border border-border bg-card shadow-lg">
                <div className="sticky top-0 bg-card p-2 border-b border-border z-10">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'var(--muted)' }} />
                    <input
                      ref={countrySearchRef}
                      type="text"
                      className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder={t('common.searchCountries')}
                      value={countryQuery}
                      onChange={(e) => setCountryQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') setIsCountryOpen(false); }}
                    />
                  </div>
                </div>

                {priorityList.map(country => (
                  <div
                    key={country.code}
                    onClick={() => handleCountrySelect(country)}
                    className={cn(
                      'flex items-center gap-2.5 px-3 py-2 cursor-pointer text-sm hover:bg-hover',
                      selectedCountry.code === country.code && 'bg-primary/10'
                    )}
                  >
                    <span className="text-lg leading-none">{country.flag}</span>
                    <span className="flex-1 text-foreground truncate">{country.name}</span>
                    <span className="text-xs font-medium" style={{ color: 'var(--muted)' }}>{country.dialCode}</span>
                    {selectedCountry.code === country.code && (
                      <CheckIcon className="h-4 w-4 text-primary flex-shrink-0" />
                    )}
                  </div>
                ))}

                {priorityList.length > 0 && otherList.length > 0 && (
                  <div className="border-t border-border my-1" />
                )}

                {otherList.map(country => (
                  <div
                    key={country.code}
                    onClick={() => handleCountrySelect(country)}
                    className={cn(
                      'flex items-center gap-2.5 px-3 py-2 cursor-pointer text-sm hover:bg-hover',
                      selectedCountry.code === country.code && 'bg-primary/10'
                    )}
                  >
                    <span className="text-lg leading-none">{country.flag}</span>
                    <span className="flex-1 text-foreground truncate">{country.name}</span>
                    <span className="text-xs font-medium" style={{ color: 'var(--muted)' }}>{country.dialCode}</span>
                    {selectedCountry.code === country.code && (
                      <CheckIcon className="h-4 w-4 text-primary flex-shrink-0" />
                    )}
                  </div>
                ))}

                {filteredCountries.length === 0 && (
                  <div className="px-3 py-4 text-sm text-center" style={{ color: 'var(--muted)' }}>{t('common.noCountriesFound')}</div>
                )}
              </div>
            </Transition>

            {isCountryOpen && (
              <div className="fixed inset-0 z-40" onClick={() => setIsCountryOpen(false)} />
            )}
          </div>

          {/* Number input */}
          <input
            ref={numberInputRef}
            type="tel"
            value={newNumber}
            onChange={(e) => setNewNumber(e.target.value)}
            onPaste={handlePaste}
            placeholder={placeholder}
            className="flex-1 rounded-r-lg rounded-l-none border border-border bg-card text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:z-10"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSaveNew(); } }}
          />
        </div>

        {/* Label input (optional) */}
        <input
          type="text"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder={t('common.labelOptional')}
          className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSaveNew(); } }}
        />

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSaveNew}
            disabled={!newNumber.replace(/\D/g, '')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full transition-colors bg-primary text-white hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckIcon className="h-4 w-4" />
            {t('common.saveAndSelect')}
          </button>
          <button
            type="button"
            onClick={handleCancelAdd}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full hover:bg-hover transition-colors"
            style={{ color: 'var(--muted)' }}
          >
            <XMarkIcon className="h-4 w-4" />
            {t('common.cancel')}
          </button>
        </div>
      </div>
    );
  }

  // --- Default Mode: Dropdown with saved numbers ---
  return (
    <div className={cn('relative mt-1', className)} ref={containerRef}>
      {/* Main dropdown button */}
      <button
        type="button"
        onClick={() => setIsDropdownOpen(prev => !prev)}
        disabled={disabled}
        id={id}
        className={cn(
          'w-full flex items-center justify-between px-3 py-2 rounded-lg border border-border',
          'bg-card text-sm transition-colors duration-200',
          'hover:bg-hover focus:outline-none focus:ring-2 focus:ring-primary',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
        style={{ minHeight: '38px' }}
      >
        {currentDisplay ? (
          <div className="flex items-center gap-2">
            {currentDisplay.flag && <span className="text-lg leading-none">{currentDisplay.flag}</span>}
            <span className="text-foreground">{value}</span>
            {/* Show label if saved */}
            {savedNumbers.find(n => n.phone === value)?.label && (
              <span className="text-xs" style={{ color: 'var(--muted)' }}>
                ({savedNumbers.find(n => n.phone === value).label})
              </span>
            )}
          </div>
        ) : (
          <span style={{ color: 'var(--muted)' }}>{t('common.selectANumber')}</span>
        )}
        <ChevronUpDownIcon className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--muted)' }} />
      </button>

      {/* Dropdown panel */}
      <Transition
        show={isDropdownOpen}
        as={Fragment}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-auto rounded-xl border border-border bg-card shadow-lg">
          {/* Saved numbers */}
          {savedNumbers.length > 0 ? (
            <>
              <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide bg-background border-b border-border" style={{ color: 'var(--muted)' }}>
                {t('common.savedNumbers')}
              </div>
              {savedNumbers.map((item) => {
                const display = formatDisplayNumber(item.phone);
                const isSelected = value === item.phone;
                return (
                  <div
                    key={item.phone}
                    onClick={() => handleSelectSaved(item.phone)}
                    className={cn(
                      'flex items-center gap-2.5 px-3 py-2.5 cursor-pointer text-sm hover:bg-hover',
                      isSelected && 'bg-primary/10'
                    )}
                  >
                    {display.flag && <span className="text-lg leading-none">{display.flag}</span>}
                    <div className="flex-1 min-w-0">
                      <div className="text-foreground font-medium">{item.phone}</div>
                      {item.label && item.label !== `${display.flag} ${item.phone}` && (
                        <div className="text-xs truncate" style={{ color: 'var(--muted)' }}>{item.label}</div>
                      )}
                    </div>
                    {isSelected && <CheckIcon className="h-4 w-4 text-primary flex-shrink-0" />}
                    <button
                      type="button"
                      onClick={(e) => handleDeleteSaved(e, item.phone)}
                      className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-500 transition-colors flex-shrink-0"
                      style={{ color: 'var(--muted)' }}
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="px-3 py-4 text-sm text-center" style={{ color: 'var(--muted)' }}>
              {t('common.noSavedNumbers')}
            </div>
          )}

          {/* Add new number button */}
          <div className="border-t border-border">
            <div
              onClick={handleStartAdd}
              className="flex items-center gap-2 px-3 py-2.5 cursor-pointer text-sm font-medium text-primary hover:bg-hover transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              {t('common.addNewNumber')}
            </div>
          </div>
        </div>
      </Transition>

      {/* Click outside to close */}
      {isDropdownOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)} />
      )}
    </div>
  );
});

PhoneInput.displayName = 'PhoneInput';

export { PhoneInput };
